import React, { memo, useContext, useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import BlueButton from "../../../../../components/BlueButton";
import CloseIcon from "@mui/icons-material/Close";
import WhiteButton from "../../../../../components/WhiteButton";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import { keyframes } from "@emotion/react";
import styles from "./styles.module.css";
import dayjs from "dayjs";
import InputAdornment from "@mui/material/InputAdornment";
import SnackBar from "../../../../../components/SnackBar";
import MensagemSucesso from "../../../../../components/MensagemSucesso";
import MensagemErro from "../../../../../components/MensagemErro";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import GenericDatePickerReference from "../../../../../components/GenericDatePickerReference";
import { debounce } from "lodash";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { exportExcel } from "../../../../../utils/exportExcelFunction";
import TableCardv2 from "../../../../../components/TableCardv2";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { PreviaInssContext } from "../../../PreviaInssContext";

const Relatorio20s = memo(() => {
  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idBaseCalculo, setIdBaseCalculo] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [openModalInsert, setOpenModalInsert] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [referencia, setReferencia] = useState("");
  const [valor, setValor] = useState({ content: "0", error: false });
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [openSnackSucesso, setOpenSnackSucesso] = useState(false);
  const [openSnackErro, setOpenSnackErro] = useState(false);
  const [msg, setMsg] = useState(null);
  const { dados, setDados, taxaSelic, handleAtualizarVersao } = useContext(PreviaInssContext);

  // normalize editability: if undefined treat as editable (true)
  const isEditable = typeof dados?.isEditable === "undefined" ? true : Boolean(dados.isEditable);

  // Helper to show not-editable message
  const showNotEditableMessage = () => {
    // Match Impostos behavior: only show the error message. Do NOT alter loading here.
    setMsg("Esta versão não permite alterações.");
    setOpenSnackErro(true);
  };

  const handleCloseSnackSucesso = (_, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackSucesso(false);
  };

  const handleCloseSnackErro = (_, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackErro(false);
  };

  const taxa20s = Number(5.8);
  const valorDiferencaBase = Number(30360);

  const columns = [
    { key: "data", label: "Referência" },
    { key: "valor", label: "Base de Cálculo" },
    { key: "diferencaBase", label: "Diferença Base" },
    { key: "calculo20s", label: "Cálculo 20S" },
    { key: "taxaSelic", label: "Taxa Selic" },
    { key: "acrescimoSelic", label: "Acréscimo Selic" },
    { key: "valorAtualizado", label: "Valor Atualizado" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const sortedBases = [...dados.baseCalculo].sort(
        (a, b) => dayjs(a.referencia).unix() - dayjs(b.referencia).unix()
      );
      const responseOrganizada = handleOrganizaData(sortedBases);
      setDataTable(responseOrganizada);

      await handleAtualizarVersao({ ...dados, baseCalculo: sortedBases });
    };
    fetchData();
  }, [dados.baseCalculo, taxaSelic]);

  const handleOpenModalInsert = async () => {
    setOpenModalInsert(true);
  };

  const handleOpenModalDelete = async () => {
    setOpenModalDelete(true);
  };

  const handleCloseModalInsert = () => {
    setOpenModalInsert(false);
    setIdBaseCalculo(null);
    setReferencia({ content: null, error: false });
    setValor({ content: "0", error: false });
  };

  const handleCloseModalDelete = () => {
    setOpenModalDelete(false);
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value == null) return "";
    return String(value).replace(".", ",");
  };

  const getTaxaSelicPorReferencia = (referencia) => {
    if (!referencia || !taxaSelic || taxaSelic.length === 0) return "0";

    const referenciaFormatada = dayjs(referencia).format("YYYY-MM");
    const taxaEncontrada = taxaSelic.find(
      (item) => item.referencia === referenciaFormatada
    );

    return taxaEncontrada ? taxaEncontrada.taxa : "0";
  };

  const addTableCellValues = (basesCalculo) => {
    return basesCalculo.map((base) => {
      base.referencia_cellval =
        base.referencia.split("-")[1] + "/" + base.referencia.split("-")[0];
      base.valor = parseFloat(base.valor);
      base.valor_cellval = parseFloat(base.valor);

      base.diferencaBase =
        parseFloat(base.valor) - parseFloat(valorDiferencaBase) > 0
          ? parseFloat(base.valor) - parseFloat(valorDiferencaBase)
          : 0;
      base.diferencaBase_cellVal = base.diferencaBase;

      base.calculo20s =
        parseFloat((base.diferencaBase * taxa20s) / 100) > 0
          ? parseFloat((base.diferencaBase * taxa20s) / 100)
          : 0;
      base.calculo20s_cellVal = parseFloat(base.calculo20s);

      base.taxaSelic_cellVal = base.taxaSelic
        ? parseFloat(base.taxaSelic)
        : "0.00";

      base.acrescimo_selic =
        parseFloat(base.calculo20s) * base.taxaSelic > 0
          ? (parseFloat(base.calculo20s) * base.taxaSelic) / 100
          : 0;

      base.acrescimo_selic_cellVal = !base.taxaSelic
        ? ""
        : base.acrescimo_selic
        ? base.acrescimo_selic
        : "0.00";

      base.valor_atualizado =
        base.calculo20s + base.acrescimo_selic > 0
          ? parseFloat(base.calculo20s + base.acrescimo_selic)
          : 0;

      base.valor_atualizado_cellVal = !base.taxaSelic
        ? ""
        : parseFloat(base.valor_atualizado)
        ? parseFloat(base.valor_atualizado)
        : "0.00";

      return base;
    });
  };

  const handleOrganizaData = (dados) => {
    return addTableCellValues(dados).map((item) => {
      return {
        data: dayjs(item.referencia).format("MM/YYYY"),
        valor: formatCurrency(item.valor),
        diferencaBase: formatCurrency(item.diferencaBase),
        calculo20s: formatCurrency(item.calculo20s),
        taxaSelic: item.taxaSelic ? `${item.taxaSelic}%` : "0%",
        acrescimoSelic: formatCurrency(item.acrescimo_selic),
        valorAtualizado: formatCurrency(item.valor_atualizado),
        btn: [
          {
            icon: <EditIcon />,
            title: "EDITAR",
            onClick: async () => {
              try {
                // Match Impostos: use falsy check via normalized flag
                if (!isEditable) {
                  showNotEditableMessage();
                  return;
                }
                setIsEdit(true);
                setIdBaseCalculo(item.idBaseCalculo);
                setReferencia(item.referencia);
                setValor({ content: item.valor, error: false });
                handleOpenModalInsert();
              } catch (error) {
                console.log(error);
              }
            },
          },
          {
            icon: <DeleteForeverIcon />,
            title: "EXCLUIR",
            onClick: () => {
              if (!isEditable) {
                showNotEditableMessage();
                return;
              }
              setIdBaseCalculo(item.idBaseCalculo);
              handleOpenModalDelete();
            },
          },
        ],
      };
    });
  };

  const handleDebounce = debounce((value) => {
    handlePesquisaPorTexto(value);
  }, 500);

  const handlePesquisaPorTexto = (value) => {
    if (!value) {
      const sortedBases = [...dados.baseCalculo].sort(
        (a, b) => dayjs(a.referencia).unix() - dayjs(b.referencia).unix()
      );
      setDataTable(handleOrganizaData(sortedBases));
      return;
    }

    const searchValue = value.toLowerCase();
    const filtered = dados.baseCalculo.filter((item) => {
      return (
        item.valor.toString().includes(searchValue) ||
        dayjs(item.referencia).format("MM/YYYY").includes(searchValue)
      );
    });

    const sortedFiltered = [...filtered].sort(
      (a, b) => dayjs(a.referencia).unix() - dayjs(b.referencia).unix()
    );
    setDataTable(handleOrganizaData(sortedFiltered));
  };

  const handleSubmitInsert = (e) => {
    e.preventDefault();
    // Match Impostos semantics: any falsy is treated as non-editable via normalized flag
    if (!isEditable) {
      showNotEditableMessage();
      return;
    }
    setLoading(true);

    const referenciaFormatada = dayjs(referencia.content).format("YYYY-MM");
    const taxaSelicEncontrada = getTaxaSelicPorReferencia(referencia.content);

    const newBaseCalculo = {
      idBaseCalculo:
        Math.max(0, ...dados.baseCalculo.map((i) => i.idBaseCalculo)) + 1,
      referencia: referenciaFormatada,
      valor: valor.content || "0",
      taxaSelic: taxaSelicEncontrada,
    };

    try {
      const updatedBases = [...dados.baseCalculo, newBaseCalculo].sort(
        (a, b) => dayjs(a.referencia).unix() - dayjs(b.referencia).unix()
      );

      setDados((prev) => ({
        ...prev,
        baseCalculo: updatedBases,
      }));

      setObjSnackBar({
        type: "success",
        msg: "Base de cálculo adicionada com sucesso!",
      });
      setMsg("Base de cálculo adicionada com sucesso!");
      setOpenSnackSucesso(true);
      handleCloseModalInsert();
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: "Erro ao adicionar base de cálculo",
      });
      setMsg("Erro ao adicionar base de cálculo");
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdate = (e) => {
    e.preventDefault();
    // Match Impostos semantics
    if (!isEditable) {
      showNotEditableMessage();
      return;
    }
    setLoading(true);

    try {
      // normalize referencia from state (can be object {content} or string)
      const referenciaFormatada = (() => {
        if (!referencia) return null;
        if (typeof referencia === 'object' && referencia.content) {
          try { return dayjs(referencia.content).format('YYYY-MM'); } catch { return String(referencia.content); }
        }
        // already a string like 'YYYY-MM' or similar
        return String(referencia);
      })();

      const updatedBases = dados.baseCalculo
        .map((item) =>
          item.idBaseCalculo === idBaseCalculo
            ? {
                ...item,
                valor: valor.content || "0",
                referencia: referenciaFormatada || item.referencia,
                taxaSelic: getTaxaSelicPorReferencia(referenciaFormatada || item.referencia),
              }
            : item
        )
        .sort(
          (a, b) => dayjs(a.referencia).unix() - dayjs(b.referencia).unix()
        );

      setDados((prev) => ({
        ...prev,
        baseCalculo: updatedBases,
      }));

      setObjSnackBar({
        type: "success",
        msg: "Base de cálculo atualizada com sucesso!",
      });
      setMsg("Base de cálculo atualizada com sucesso!");
      setOpenSnackSucesso(true);
      handleCloseModalInsert();
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: "Erro ao atualizar base de cálculo",
      });
      setMsg("Erro ao atualizar base de cálculo");
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDelete = (e) => {
    e.preventDefault();
    // Match Impostos semantics
    if (!isEditable) {
      showNotEditableMessage();
      return;
    }
    setLoading(true);

    try {
      const updatedBases = dados.baseCalculo
        .filter((item) => item.idBaseCalculo !== idBaseCalculo)
        .sort(
          (a, b) => dayjs(a.referencia).unix() - dayjs(b.referencia).unix()
        );

      setDados((prev) => ({
        ...prev,
        baseCalculo: updatedBases,
      }));

      setObjSnackBar({
        type: "success",
        msg: "Base de cálculo excluída com sucesso!",
      });
      setMsg("Base de cálculo excluída com sucesso!");
      setOpenSnackSucesso(true);
      handleCloseModalDelete();
    } catch (error) {
      setObjSnackBar({ type: "error", msg: "Erro ao excluir base de cálculo" });
      setMsg("Erro ao excluir base de cálculo");
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  };
  

  const handleChangeValue = (name, value) => {
    switch (name) {
      case "referencia":
        if (!isNaN(value.getTime())) {
          setReferencia({
            content: value,
            error: value != null ? false : true,
          });
        } else {
          setReferencia({
            content: value,
            error: true,
          });
        }
        break;
      case "valor":
        const masked = value.replace(/,/g, ".");
        setValor({ content: masked, error: masked === "" });
        break;
    }
  };

  const handleChangeReferencia = (newValue) => {
    setReferencia({ content: newValue, error: false });
  };

  const handleFunctionExportExcel = async () => {
    try {
      const formattedData = dataTable.map((item) => ({
        Referência: item.data,
        "Base de Cálculo": item.valor,
        "Diferença Base": item.diferencaBase,
        "Cálculo 20S": item.calculo20s,
        "Taxa Selic": parseFloat(item.taxaSelic.replace("%", "")),
        "Acréscimo Selic": item.acrescimoSelic,
        "Valor Atualizado": item.valorAtualizado,
      }));

      const totalRow = {
        Referência: "Total",
        "Base de Cálculo": formattedData.reduce(
          (sum, row) =>
            sum +
            parseFloat(
              row["Base de Cálculo"].replace(/[^0-9,-]/g, "").replace(",", ".")
            ),
          0
        ),
        "Diferença Base": formattedData.reduce(
          (sum, row) =>
            sum +
            parseFloat(
              row["Diferença Base"].replace(/[^0-9,-]/g, "").replace(",", ".")
            ),
          0
        ),
        "Cálculo 20S": formattedData.reduce(
          (sum, row) =>
            sum +
            parseFloat(
              row["Cálculo 20S"].replace(/[^0-9,-]/g, "").replace(",", ".")
            ),
          0
        ),
        "Taxa Selic": formattedData.reduce(
          (sum, row) => sum + row["Taxa Selic"],
          0
        ),
        "Acréscimo Selic": formattedData.reduce(
          (sum, row) =>
            sum +
            parseFloat(
              row["Acréscimo Selic"].replace(/[^0-9,-]/g, "").replace(",", ".")
            ),
          0
        ),
        "Valor Atualizado": formattedData.reduce(
          (sum, row) =>
            sum +
            parseFloat(
              row["Valor Atualizado"].replace(/[^0-9,-]/g, "").replace(",", ".")
            ),
          0
        ),
      };

      const valoresBaseRow = {
        Referência: "Valores Base",
        "Base de Cálculo": 0,
        "Diferença Base": 30360,
        "Cálculo 20S": 0.058,
        "Taxa Selic": null,
        "Acréscimo Selic": 0,
        "Valor Atualizado": 0,
      };

      formattedData.push({});
      formattedData.push(totalRow);
      formattedData.push({});
      formattedData.push(valoresBaseRow);

      await exportExcel([formattedData, "bases_calculo"]);
      setObjSnackBar({
        type: "success",
        msg: "Arquivo Excel gerado com sucesso!",
      });
      setMsg("Arquivo Excel gerado com sucesso!");
      setOpenSnackSucesso(true);
    } catch (error) {
      setObjSnackBar({ type: "error", msg: "Erro ao exportar para Excel" });
      setMsg("Erro ao exportar para Excel");
      setOpenSnackErro(true);
    }
  };

  const Complementary = () => {
    return (
      <div style={{ display: "flex", gap: "1rem" }}>
        <TextField
          disabled
          error={valorDiferencaBase.error}
          value={formatCurrency(valorDiferencaBase)}
          size="small"
          label="Valor Diferença Base"
          sx={{ width: "10rem" }}
        />

        <TextField
          disabled
          error={taxa20s.error}
          value={formatPercentage(taxa20s)}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          size="small"
          label="Taxa 20S"
          sx={{ width: "7rem" }}
        />
        <Button
          startIcon={<DownloadRoundedIcon />}
          variant="outlined"
          onClick={handleFunctionExportExcel}
          size="large"
          className={styles.btnComplementary}
        >
          Exportar Relatório
        </Button>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          variant="outlined"
          onClick={() => {
            if (!isEditable) {
              showNotEditableMessage();
              return;
            }
            setIsEdit(false);
            handleOpenModalInsert();
          }}
          size="large"
          className={styles.btnComplementary}
          disabled={!isEditable}
        >
          Adicionar
        </Button>
      </div>
    );
  };

  return (
    <>
      <div>
        <TableCardv2
          data={dataTable}
          columns={columns}
          loading={loading}
          debounce={handleDebounce}
          complementaryHeaderTop={<Complementary />}
        />
        <Dialog
          fullWidth
          open={openModalInsert}
          onClose={handleCloseModalInsert}
        >
          <DialogTitle>
            <h4>{isEdit ? "Atualizar" : "Inserir"} Base de Cálculo</h4>
          </DialogTitle>
          <DialogContent>
            <form onSubmit={isEdit ? handleSubmitUpdate : handleSubmitInsert}>
              <Grid container spacing={2} sx={{ marginBottom: "1rem" }}>
                {!isEdit && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <GenericDatePickerReference
                        label={"Referência"}
                        value={referencia.content}
                        onChange={handleChangeReferencia}
                        sx={{ marginTop: "1rem" }}
                        disabled={!isEditable}
                      />
                    </Grid>
                  </>
                )}
                {isEdit && (
                  <Grid item xs={12} sm={6}>
                    <GenericDatePickerReference
                      label={`Referência`}
                      value={referencia}
                      onChange={(value) =>
                        setReferencia(dayjs(value).format("YYYY-MM"))
                      }
                      sx={{ marginTop: "1rem" }}
                      disabled={!isEditable}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    error={valor.error}
                    value={valor.content}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }}
                    onChange={(event) =>
                      handleChangeValue("valor", event.target.value)
                    }
                    fullWidth
                    size="small"
                    label="Base de Cálculo"
                    sx={{ marginTop: "1rem" }}
                    disabled={!isEditable}
                  />
                </Grid>
              </Grid>
              <DialogActions
                sx={{
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <WhiteButton
                  clickFunction={handleCloseModalInsert}
                  textValue="Fechar"
                  contentIcon={<CloseIcon />}
                  iconLeft
                />

                <BlueButton
                  textValue="Salvar"
                  type="submit"
                  disabled={!isEditable}
                />
              </DialogActions>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={openModalDelete} onClose={handleCloseModalDelete}>
          <DialogContent>
            <h2 style={{ fontSize: "1.1rem" }}>
              Deseja mesmo excluir essa Base de Cálculo?
            </h2>
            <div className={styles.containerButtons}>
              <WhiteButton
                textValue="Não"
                contentIcon={<CloseIcon />}
                clickFunction={handleCloseModalDelete}
                iconLeft
              />
              <BlueButton
                textValue="Sim"
                contentIcon={<CheckIcon />}
                clickFunction={handleSubmitDelete}
                disabled={!isEditable}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
      <MensagemSucesso open={openSnackSucesso} msg={msg} handleClose={handleCloseSnackSucesso} />
      <MensagemErro open={openSnackErro} msg={msg} handleClose={handleCloseSnackErro} />
    </>
  );
});

export default Relatorio20s;
